import { useCallback, useEffect, useState, type ChangeEvent } from 'react';
import { ArrowLeftRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface ConfirmArrivalQuantityInputProps {
  id: string;
  /** Value in the unit currently displayed — already converted by the caller. */
  value: number;
  onChange: (next: number) => void;
  /** Ceiling in display units. Typing past it snaps the field back immediately. */
  max: number;
  unitLabel: string;
  /** Turns the unit label into a button that shows or hides the cell's input in the other unit. */
  unitToggle?: { label: string; expanded: boolean; controls: string; onToggle: () => void };
  className?: string;
  disabled?: boolean;
  invalid?: boolean;
  'aria-label': string;
}

/**
 * A quantity cell.
 *
 * It keeps its own draft string while focused so the field can be cleared and
 * retyped: the form itself only ever holds a number, and echoing that number
 * straight back into the input would refill an emptied field with `0` mid-keystroke.
 */
export function ConfirmArrivalQuantityInput({
  id,
  value,
  onChange,
  max,
  unitLabel,
  unitToggle,
  className,
  disabled,
  invalid,
  'aria-label': ariaLabel
}: ConfirmArrivalQuantityInputProps) {
  const [draft, setDraft] = useState(() => String(value));
  const [isFocused, setIsFocused] = useState(false);

  // Adopt outside changes (reset quantities, a unit-mode switch, another bucket
  // borrowing from this one) only while the user is not typing into this field.
  useEffect(() => {
    if (!isFocused) setDraft(String(value));
  }, [value, isFocused]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;

      // An emptied field reads as zero, but the draft stays empty so the user
      // can type a replacement instead of fighting a re-inserted "0".
      if (raw === '') {
        setDraft(raw);
        onChange(0);
        return;
      }

      const parsed = Number(raw);
      // Intermediate states a number input still emits ("1e", "-"): keep them
      // visible, but do not push a NaN into the form.
      if (!Number.isFinite(parsed)) {
        setDraft(raw);
        return;
      }

      // Whole numbers only, rounded down, and clamped here rather than only in
      // the form: both have to show up in the field the instant they apply.
      // Leaving the draft on the typed number would let the input disagree with
      // the value the form actually holds.
      const clamped = Math.min(Math.max(Math.floor(parsed), 0), max);
      setDraft(clamped === parsed ? raw : String(clamped));
      onChange(clamped);
    },
    [max, onChange]
  );

  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      <Input
        id={id}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        type="number"
        min={0}
        max={max}
        step={1}
        inputMode="numeric"
        disabled={disabled}
        className={cn('h-9 w-24 text-right tabular-nums', invalid && 'border-destructive')}
        value={draft}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onChange={handleChange}
      />
      {unitToggle ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="xs"
              className={cn(
                'whitespace-nowrap font-normal text-muted-foreground',
                unitToggle.expanded && 'bg-accent text-accent-foreground'
              )}
              aria-label={unitToggle.label}
              aria-expanded={unitToggle.expanded}
              aria-controls={unitToggle.controls}
              disabled={disabled}
              onClick={unitToggle.onToggle}>
              {unitLabel}
              <ArrowLeftRight />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{unitToggle.label}</TooltipContent>
        </Tooltip>
      ) : (
        <span className="whitespace-nowrap text-xs text-muted-foreground">{unitLabel}</span>
      )}
    </div>
  );
}
